import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import { useSearchReveal } from '../hooks/useSearchReveal';
import type { OutreachLiveSearch } from '../outreachTypes';
import { SearchMatchRow } from './SearchMatchRow';
import { liveMessages as messages } from './liveMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the live screens; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
import styles from './Live.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SearchMatchesProps {
  campaignUuid: string;
  song: string;
  search: OutreachLiveSearch;
  /** The footer: "Nothing goes out yet" and Pause. */
  footer: React.ReactNode;
}

/**
 * "Curators who fit <song>" (design t1): the real matches, best fit first. The rows that Plug found
 * since the last view appear one by one (`useSearchReveal`). The count is the API's `matchCount`.
 */
const SearchMatchesComponent: React.FC<SearchMatchesProps> = ({ campaignUuid, song, search, footer }) => {
  const intl = useIntl();
  const { visible, justFound } = useSearchReveal(campaignUuid, search.matches);
  const rows = useMemo(
    () =>
      visible.map((match) => (
        <SearchMatchRow key={match.targetUuid} match={match} isNew={justFound.has(match.targetUuid)} />
      )),
    [justFound, visible]
  );

  return (
    <section className={styles.card} aria-labelledby="outreach-search-matches">
      <div className={shared.row}>
        <h2 id="outreach-search-matches" className={styles.h2}>
          {intl.formatMessage(messages.matchesTitle, { song })}
        </h2>
        <span className={clsx(shared.chip, shared.chipVio)} data-testid="search-match-count">
          {intl.formatMessage(messages.foundSoFar, { count: intl.formatNumber(search.matchCount) })}
        </span>
        <span className={`${shared.small} ms-auto`}>{intl.formatMessage(messages.bestFitFirst)}</span>
      </div>
      <ul className={styles.matches} aria-live="polite">
        {rows}
        {search.matches.length === 0 && <li className={shared.small}>{intl.formatMessage(messages.noMatchesYet)}</li>}
        {search.playlistsLeft !== null && (
          <li className={clsx(styles.match, styles.matchMore)} data-testid="search-playlists-left">
            <span className={styles.spin} aria-hidden />
            {intl.formatMessage(messages.playlistsLeft, { count: search.playlistsLeft })}
          </li>
        )}
      </ul>
      {footer}
    </section>
  );
};

export const SearchMatches = TypedMemo(SearchMatchesComponent);
